import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SecurityIcon from '@mui/icons-material/Security';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import { apiClient } from '../../utils/auth';
import { buildProposalMediaUrl } from '../../utils/mediaUrls';

export default function BidderProfile() {
  const { anonymousId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract query parameters for bid context
  const searchParams = new URLSearchParams(location.search);
  const reqId = searchParams.get('reqId');
  const bidId = searchParams.get('bidId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog actions for unlocking/agreement
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'unlock' or 'agreement'
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [bidContext, setBidContext] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const requests = [apiClient.get(`/api/v1/bidders/${anonymousId}/`)];
        if (reqId && bidId) {
          requests.push(apiClient.get(`/api/v1/requirements/${reqId}/bids/`));
        }
        const [profileRes, bidsRes] = await Promise.all(requests);
        setProfile(profileRes.data);
        if (bidsRes?.data) {
          const bids = bidsRes.data.bids || bidsRes.data;
          const match = (Array.isArray(bids) ? bids : []).find((b) => String(b.id) === String(bidId));
          setBidContext(match || null);
        }
      } catch (err) {
        setError('Failed to retrieve bidder profile data. Please verify authorization.');
      } finally {
        setLoading(false);
      }
    })();
  }, [anonymousId, reqId, bidId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAction = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      if (dialogType === 'agreement') {
        if (!bidId) {
          setActionError('No active bid ID context found. Select this bidder directly from your bids list.');
          setActionLoading(false);
          return;
        }
        // Select bidder as winner and establish agreement
        const { data } = await apiClient.post(`/api/v1/bids/${bidId}/select/`);
        setDialogOpen(false);
        const dealId = data?.deal?.deal_id;
        navigate(dealId ? `/deals/${dealId}` : '/deals/mine', { replace: true });
      } else {
        // Unlock action description
        setDialogOpen(false);
      }
    } catch (err) {
      setActionError(err.response?.data?.error?.message || 'Failed to execute action. Please check KYC status.');
    } finally {
      setActionLoading(false);
    }
  };

  const openActionDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
    setActionError('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', bgcolor: '#080d1a' }}>
        <CircularProgress aria-label="Loading bidder profile" />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ maxWidth: 860, mx: 'auto', mt: 6, p: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            borderColor: 'rgba(99, 102, 241, 0.4)',
            color: '#818cf8',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
            }
          }}
        >
          Back
        </Button>
        <Alert severity="error">{error || 'Profile not found.'}</Alert>
      </Box>
    );
  }

  // Cryptographic abbreviation of UUID for display (e.g. BD-7529)
  const shortId = anonymousId ? anonymousId.split('-')[0].toUpperCase().substring(0, 4) : '7529';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#080d1a', color: '#f8fafc', py: 4, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Dedicated Back Button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(reqId ? `/buyer/requirements/${reqId}/bids` : -1)}
          sx={{
            mb: 4,
            borderColor: 'rgba(99, 102, 241, 0.4)',
            color: '#818cf8',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
            }
          }}
        >
          {reqId ? 'Back to Bids' : 'Back'}
        </Button>

        {/* Header Profile Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ maxWidth: '100%' }}>
            <Typography variant="h3" fontWeight={700} sx={{ color: '#ffffff', letterSpacing: '-0.025em', wordBreak: 'break-all' }}>
              {anonymousId}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Anonymous Profile
            </Typography>
          </Box>
        </Box>

        {/* Stat Cards Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Trust Score Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#10b981', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Trust Score
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                  {profile.trust_score}
                </Typography>
                <Box sx={{ mt: 2, width: '100%', height: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ width: `${profile.trust_score}%`, height: '100%', bgcolor: '#10b981' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5 }}>
                  {profile.completed_deals > 0 ? `Based on ${profile.completed_deals} deals` : 'No deals completed'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Win Rate Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#3b82f6', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Win Rate
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                  {profile.win_rate !== null ? `${profile.win_rate}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5 }}>
                  {profile.completed_deals} completed deals
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Satisfaction Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#f59e0b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Satisfaction
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                  {profile.satisfaction !== null ? `${profile.satisfaction}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5 }}>
                  {profile.ratings_count > 0 ? `Based on ${profile.ratings_count} reviews` : 'No reviews yet'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Response Time Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)',
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#a855f7', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Response Time
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                  {profile.completed_deals > 0 ? profile.response_time : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5 }}>
                  {profile.completed_deals > 0 ? 'Average responder' : 'No deals completed'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Details and Sidebar */}
        <Grid container spacing={4}>
          {/* Tabs Details Section */}
          <Grid item xs={12} md={8}>
            <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.08)', mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    color: '#94a3b8',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                    minWidth: 100,
                    '&.Mui-selected': { color: '#ffffff' }
                  },
                  '& .MuiTabs-indicator': { bgcolor: '#ffffff', height: 2 }
                }}
              >
                <Tab label="Overview" />
                <Tab label="Skills" />
                <Tab label="Performance" />
              </Tabs>
            </Box>

            {/* Overview Tab Content */}
            {activeTab === 0 && (
              <Box>
                {/* Badges Section */}
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff', mb: 2 }}>
                  🏅 Earned Badges
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                  {profile.badges?.map((badge, idx) => {
                    const badgeColors = {
                      'Top Rated': { color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)', bg: 'rgba(251, 191, 36, 0.05)' },
                      'Expert': { color: '#fb7185', border: '1px solid rgba(251, 113, 133, 0.3)', bg: 'rgba(251, 113, 133, 0.05)' },
                      'Verified': { color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)', bg: 'rgba(52, 211, 153, 0.05)' },
                      'Quick Response': { color: '#60a5fa', border: '1px solid rgba(96, 165, 250, 0.3)', bg: 'rgba(96, 165, 250, 0.05)' },
                      'Long Term': { color: '#818cf8', border: '1px solid rgba(129, 140, 248, 0.3)', bg: 'rgba(129, 140, 248, 0.05)' },
                    };
                    const colorSet = badgeColors[badge.name] || { color: '#ffffff', border: '1px solid rgba(255,255,255,0.1)', bg: 'rgba(255,255,255,0.05)' };
                    return (
                      <Chip
                        key={idx}
                        icon={<span style={{ marginRight: 4 }}>{badge.icon}</span>}
                        label={badge.name}
                        sx={{
                          color: colorSet.color,
                          border: colorSet.border,
                          bgcolor: colorSet.bg,
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1,
                          '& .MuiChip-icon': { color: 'inherit' }
                        }}
                      />
                    );
                  })}
                </Box>

                {/* Certifications Section */}
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff', mb: 2 }}>
                  🛡️ Verified Certifications
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                  {profile.certifications?.length >= 5 ? '5+' : (profile.certifications?.length || 0)} active certifications
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {profile.certifications?.map((cert, idx) => (
                    <Card key={idx} sx={{
                      bgcolor: 'rgba(13, 21, 38, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.04)',
                      borderRadius: 3,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#10b981',
                        boxShadow: '0 0 15px rgba(16, 185, 129, 0.15)',
                        transform: 'translateY(-1px)'
                      }
                    }}>
                      <CardContent sx={{ p: '20px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: 'rgba(16, 185, 129, 0.06)',
                            border: '1px solid rgba(16, 185, 129, 0.15)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}>
                            <SecurityIcon sx={{ color: '#10b981', fontSize: 22 }} />
                          </Box>
                          <Box>
                            <Typography variant="body1" fontWeight={600} sx={{ color: '#f8fafc', fontSize: 16 }}>
                              {cert.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                              {cert.desc} • {cert.issuer}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={cert.year}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.04)',
                            color: '#cbd5e1',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            fontWeight: 600
                          }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Skills Tab Content */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff', mb: 3 }}>
                  🔒 Expertise Matrix & Completed Projects
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {profile.skills?.map((skill, idx) => (
                    <Box key={idx}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" fontWeight={500} sx={{ color: '#cbd5e1' }}>
                          {skill.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                          {skill.completed_projects} projects completed •{' '}
                          <span style={{
                            color: skill.level === 'Expert' ? '#f59e0b' : skill.level === 'Advanced' ? '#818cf8' : skill.level === 'Intermediate' ? '#34d399' : skill.level === 'Beginner' ? '#38bdf8' : '#64748b',
                            fontWeight: 600
                          }}>
                            {skill.level}
                          </span>
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 6, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                        <Box sx={{
                          width: `${Math.min(100, (skill.completed_projects / 35) * 100)}%`,
                          height: '100%',
                          bgcolor: skill.level === 'Expert' ? '#f59e0b' : skill.level === 'Advanced' ? '#818cf8' : skill.level === 'Intermediate' ? '#34d399' : skill.level === 'Beginner' ? '#38bdf8' : 'transparent',
                          borderRadius: 1
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Performance Tab Content */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff', mb: 3 }}>
                  📈 Detailed Performance Verification
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Success Rate</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                          {profile.performance.success_rate !== null ? `${profile.performance.success_rate}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Successful project delivery against total accepted contracts.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>On-Time Delivery</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                          {profile.performance.on_time_delivery !== null ? `${profile.performance.on_time_delivery}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Proportion of projects closed strictly on or before set deadlines.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Milestone Completion</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                          {profile.performance.milestone_completion !== null ? `${profile.performance.milestone_completion}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Completed micro-milestones against assigned contract milestones.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Client Rating Engine</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                          {profile.performance.client_rating !== null ? `${profile.performance.client_rating} / 5.0` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Average score calculated across technical execution and documentation.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Active Disputes</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>{profile.performance.active_disputes}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Currently unresolved disputes under active review.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ bgcolor: 'rgba(13, 21, 38, 0.4)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 2 }}>
                      <CardContent sx={{ p: 3.5 }}>
                        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 600 }}>Commitment Reliability</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#ffffff', mt: 1 }}>
                          {profile.performance.commitment_reliability !== null ? `${profile.performance.commitment_reliability}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                          Evaluates off-platform attempt history and bid-acceptance rate.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Grid>

          {/* Right Sidebar Column */}
          <Grid item xs={12} md={4}>
            {/* Profile Status Card */}
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              mb: 3,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#818cf8',
                boxShadow: '0 0 20px rgba(129, 140, 248, 0.15)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff', mb: 2.5 }}>
                  Profile Status
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>Verified Account</Typography>
                  <Chip label={profile.profile_status.verified_account} size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600 }} />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>Member Since</Typography>
                  <Chip label={profile.profile_status.member_since} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#cbd5e1', fontWeight: 600 }} />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>Background Check</Typography>
                  <Chip label={profile.profile_status.background_check} size="small" sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontWeight: 600 }} />
                </Box>
              </CardContent>
            </Card>

            {bidContext && (
              <Card sx={{
                bgcolor: 'rgba(13, 21, 38, 0.6)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                borderRadius: 3,
                mb: 3,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff', mb: 2 }}>
                    Bid Delivery Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1 }}>
                    <strong style={{ color: '#cbd5e1' }}>Amount:</strong>{' '}
                    Rs.{Number(bidContext.quoted_amount).toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, lineHeight: 1.6 }}>
                    <strong style={{ color: '#cbd5e1' }}>Delivery plan:</strong> {bidContext.delivery_plan}
                  </Typography>
                  {bidContext.proposal_doc_s3_key && (
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<DescriptionIcon />}
                      href={buildProposalMediaUrl(bidContext.proposal_doc_s3_key)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mb: 2,
                        textTransform: 'none',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        color: '#34d399',
                      }}
                    >
                      View Delivery Document
                    </Button>
                  )}
                  {['SUBMITTED', 'SHORTLISTED'].includes(bidContext.bid_status) && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<GavelIcon />}
                      onClick={() => openActionDialog('agreement')}
                      sx={{
                        bgcolor: '#10b981',
                        color: '#080d1a',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#059669' },
                      }}
                    >
                      Finalize This Bidder
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Lock Details / Create Agreement Card */}
            <Card sx={{
              bgcolor: 'rgba(13, 21, 38, 0.6)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(129, 140, 248, 0.3)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
              borderRadius: 3,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.15)'
              }
            }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                  <LockIcon sx={{ color: '#94a3b8', fontSize: 22 }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.5, fontWeight: 500 }}>
                  Full identity details are revealed only after contract signing is completed.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0d1526',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#f8fafc',
            borderRadius: 3,
            p: 1,
            maxWidth: 420
          }
        }}
      >
        <DialogTitle fontWeight={700}>
          {dialogType === 'agreement' ? 'Establish Project Agreement?' : 'Unlock Professional Identity?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>
            {dialogType === 'agreement'
              ? 'This action selects this anonymous bidder as the winner for your requirement. An escrow arrangement will be generated, and real-name business identities will be shared to draft the service contract under DPDP regulations.'
              : 'Unlocking profile identity information requires an active request and consent from both parties. An admin alert will be logged.'}
          </Typography>
          {dialogType === 'agreement' && bidContext && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>
                <strong>Delivery plan:</strong> {bidContext.delivery_plan}
              </Typography>
              {bidContext.proposal_doc_s3_key ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  href={buildProposalMediaUrl(bidContext.proposal_doc_s3_key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', color: '#34d399', borderColor: 'rgba(16, 185, 129, 0.4)' }}
                >
                  Open Delivery Document
                </Button>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No delivery document was attached to this bid.
                </Alert>
              )}
            </Box>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              color: '#94a3b8',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={actionLoading}
            onClick={handleAction}
            sx={{
              bgcolor: '#10b981',
              color: '#080d1a',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: '#059669' }
            }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
